import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useSetBucketPolicy, s3ExtractError, type BucketSettings } from '@/api/s3';
import { JsonEditor } from '@/components/JsonEditor';

interface TabProps {
  serviceId: string;
  bucket: string;
  data?: BucketSettings;
}

/**
 * A policy that grants read to an anonymous `Principal:"*"` makes objects
 * world-readable. We never seed such a template — a stale-looking editor plus a
 * blind Save must not be able to publish a live bucket. This detector only gates
 * an explicit confirm on a policy the user actually typed.
 */
function grantsPublicRead(json: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return false;
  }
  const stmtField = (parsed as { Statement?: unknown }).Statement;
  const statements = Array.isArray(stmtField) ? stmtField : [stmtField];
  return statements.some((s) => {
    if (!s || typeof s !== 'object') return false;
    const st = s as { Effect?: unknown; Principal?: unknown };
    if (st.Effect !== 'Allow') return false;
    const pr = st.Principal;
    if (pr === '*') return true;
    if (pr && typeof pr === 'object') {
      const aws = (pr as { AWS?: unknown }).AWS;
      return aws === '*' || (Array.isArray(aws) && aws.includes('*'));
    }
    return false;
  });
}

/**
 * JSON policy editor. Seeded with the bucket's ACTUAL current policy (empty when
 * there is none) — never with a grant template. Saving an empty editor clears
 * the policy; only a policy the user explicitly typed is ever sent.
 */
export function BucketPolicyTab({ serviceId, bucket, data }: TabProps) {
  const toast = useToast();
  const save = useSetBucketPolicy(serviceId, bucket);
  const [doc, setDoc] = useState('');
  const [confirmPublic, setConfirmPublic] = useState(false);

  useEffect(() => {
    setDoc(data?.policy ?? '');
  }, [data?.policy, bucket]);

  const trimmed = doc.trim();
  let jsonValid = true;
  if (trimmed) {
    try {
      JSON.parse(trimmed);
    } catch {
      jsonValid = false;
    }
  }

  const applySave = () => {
    setConfirmPublic(false);
    save.mutate(trimmed, {
      onSuccess: () => toast.success(trimmed ? 'Policy saved' : 'Policy cleared'),
      onError: (e) => toast.error(s3ExtractError(e, 'Failed to save policy')),
    });
  };

  const onSave = () => {
    // Public-read policies expose every object to the internet — require an
    // explicit confirm before applying one.
    if (trimmed && grantsPublicRead(trimmed)) {
      setConfirmPublic(true);
      return;
    }
    applySave();
  };

  return (
    <Section
      title="Bucket policy"
      description="JSON access policy for this bucket. An empty editor clears the policy."
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader>
            <CardTitle>Policy document</CardTitle>
          </CardHeader>
          <CardBody>
            <JsonEditor
              value={doc}
              onChange={setDoc}
              invalid={!jsonValid}
              minHeight="240px"
              ariaLabel="Bucket policy JSON"
            />
          </CardBody>
        </Card>

        <ActionBar
          start={
            <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
              {jsonValid ? '' : 'Not valid JSON.'}
            </span>
          }
          end={
            <Button variant="primary" size="sm" disabled={!jsonValid || save.isPending} onClick={onSave}>
              {save.isPending ? 'Saving…' : trimmed ? 'Save policy' : 'Clear policy'}
            </Button>
          }
        />
      </div>

      <ConfirmDialog
        open={confirmPublic}
        onOpenChange={(o) => !o && setConfirmPublic(false)}
        title="Make bucket contents public?"
        description="This policy grants anonymous (Principal: *) access. Every matching object will be readable by anyone on the internet. Continue only if that is intended."
        confirmLabel="Apply public policy"
        destructive
        loading={save.isPending}
        onConfirm={applySave}
      />
    </Section>
  );
}
